// Stage Mail-1:Mail 連携 Card。
//
// レイアウト(design §3.7):
// ┌──────────────────────────────────────────────┐
// │ Mail 連携  連携中 / 未設定 / 要確認 / 認証失敗 │
// │ ── Account リスト(最大 3 行 + add) ──         │
// │ ── 选中行展开(作成 / 編集 / 解除) ──          │
// └──────────────────────────────────────────────┘
//
// Mail-1 では:
// - system_admin のみ表示(/settings 自体の分岐は SettingsPage.tsx で実施)
// - status 計算:not_configured(0 件) / connected(全 active + password 完備) /
//   warning(active だが password 不備) / auth_failed(disconnect 済 or
//   直近テスト失敗)
// - backend 呼び出しは mailApi、test は別 endpoint(mode=input|stored)

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Plus, RefreshCw, Inbox } from "lucide-react";
import { mailApi, type MailAccount, type MailTestResult } from "../../api/mail";
import { mailV2Api, syncJobs } from "../../api/mail-v2";
import { ApiError } from "../../api/client";
import { IntegrationStatusBadge, type IntegrationStatus } from "../../components/settings/IntegrationStatusBadge";
import { MailAccountList } from "./MailAccountList";
import { MailAccountFormModal } from "./MailAccountFormModal";

type View =
  | { mode: "idle" }
  | { mode: "creating" }
  | { mode: "editing"; accountId: string };

function deriveStatus(accounts: MailAccount[]): IntegrationStatus {
  if (accounts.length === 0) return "not_configured";
  const activeAccounts = accounts.filter((a) => a.is_active);
  if (activeAccounts.length === 0) {
    // 全て連携解除済 → auth_failed (recovery を促す)
    return "auth_failed";
  }
  // 全 active に password 完備 + 接続テスト最少 1 件実施、があれば connected
  const allReady = activeAccounts.every(
    (a) => a.has_imap_password && a.has_smtp_password,
  );
  if (!allReady) return "warning";
  // active で password 完備、なのにテストしていない account があれば warning
  // (Mail-1 では接続テスト履歴を保持していないので、保守的に connected にする)
  return "connected";
}

export type MailSettingsCardProps = {
  allowedAccountTypes?: readonly ("personal" | "shared" | "other")[];
};

export const MailSettingsCard = ({ allowedAccountTypes }: MailSettingsCardProps) => {
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [view, setView] = useState<View>({ mode: "idle" });
  const [busy, setBusy] = useState<"load" | "save" | "test-imap" | "test-smtp" | "disconnect" | "repair" | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const editingAccount = useMemo(() => {
    if (view.mode !== "editing") return null;
    return accounts.find((a) => a.id === view.accountId) ?? null;
  }, [view, accounts]);

  const load = useCallback(async () => {
    setBusy("load");
    setLoadError(null);
    try {
      const data = await mailV2Api.listAccounts();
      setAccounts(data as MailAccount[]);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setLoadError(msg);
      setAccounts([]);
    } finally {
      setBusy(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSave(body: Parameters<typeof mailApi.createAccount>[0]) {
    setBusy("save");
    setTestError(null);
    try {
      if (view.mode === "creating") {
        const created = await mailV2Api.createAccount(body);
        setAccounts((prev) => [created as MailAccount, ...prev]);
        setView({ mode: "idle" });
        // V2.5: backend returns sync_job_id when initial sync job was auto-created.
        // Stash it in sessionStorage; MailV2Page picks it up on mount (or next visit)
        // and mounts MailV2SyncOverlay so the user sees sync progress instead of
        // waiting silently for Ofelia's 30-minute pickup.
        const syncJobId = (created as typeof created & { sync_job_id?: string | null })
          .sync_job_id;
        if (syncJobId) {
          try {
            window.sessionStorage.setItem(
              "mail_v25_pending_sync_job",
              syncJobId,
            );
          } catch {
            // sessionStorage may be disabled — skip overlay pickup
          }
          setTestError(
            `追加完了。メール画面で ${syncJobId.slice(0, 8)} の同期を開始します。`,
          );
        }
      } else if (view.mode === "editing") {
        const updated = await mailV2Api.updateAccount(view.accountId, body);
        setAccounts((prev) => prev.map((a) => (a.id === updated.id ? updated as MailAccount : a)));
        setView({ mode: "idle" });
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setTestError(msg);
    } finally {
      setBusy(null);
    }
  }

  async function handleDisconnect(id: string) {
    const target = accounts.find((a) => a.id === id);
    if (!target) return;
    const ok = window.confirm(
      `「${target.name} / ${target.email_address}」の連携を完全に解除しますか?\n\n` +
        `※ Mail V2.5 (2026-08-19): このアカウントの全メール履歴・下書き・添付・\n` +
        `同期カーソルが完全に削除されます。再連携するにはパスワードを再設定して\n` +
        `新規アカウントとして登録し直してください。`,
    );
    if (!ok) return;
    setBusy("disconnect");
    try {
      await mailV2Api.deleteAccount(id);
      await load();
      if (view.mode === "editing" && view.accountId === id) {
        setView({ mode: "idle" });
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      window.alert(`連携解除に失敗しました: ${msg}`);
    } finally {
      setBusy(null);
    }
  }

  async function handleRepair() {
    const target = accounts[0];
    if (!target) {
      setTestError("修復対象のメールアカウントがありません。");
      return;
    }
    setBusy("repair");
    setTestError(null);
    try {
      const res = await syncJobs.repair(target.id);
      const jobId = res.sync_job_id ?? res.existing_job_id ?? null;
      if (jobId) {
        try {
          window.sessionStorage.setItem("mail_v25_pending_sync_job", jobId);
        } catch {
          // sessionStorage may be disabled; Mail page can still pick up active jobs.
        }
        setTestError(`修復ジョブを開始しました。メール画面で ${jobId.slice(0, 8)} の進捗を確認できます。`);
      } else {
        setTestError("修復ジョブを開始しました。");
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setTestError(`修復ジョブの作成に失敗しました: ${msg}`);
    } finally {
      setBusy(null);
    }
  }

  async function runTest(
    provider: "imap" | "smtp",
    body:
      | { mode: "input"; config: import("../../api/mail").MailSecurityBlock }
      | { mode: "stored" },
  ): Promise<MailTestResult> {
    setBusy(provider === "imap" ? "test-imap" : "test-smtp");
    setTestError(null);
    try {
      const endpoint = provider === "imap" ? mailApi.testImap : mailApi.testSmtp;
      // 新規作成モードは accountId が不定。MailAccountForm が input mode
      // のみ呼ばれる(編集時の stored は親側で別途起動する想定)。
      const accountId = view.mode === "editing" ? view.accountId : "";
      // input mode で password が空 / null / undefined の場合は補完してから投げる
      // バックエンド required なので空送信は 400 になるため、form 側で防ぐ。
      const result = accountId
        ? await endpoint(accountId, body as Parameters<typeof endpoint>[1])
        : provider === "imap"
          ? await mailApi.testImapInput(body as Parameters<typeof mailApi.testImapInput>[0])
          : await mailApi.testSmtpInput(body as Parameters<typeof mailApi.testSmtpInput>[0]);
      return result;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      throw new Error(msg);
    } finally {
      setBusy(null);
    }
  }

  const status = deriveStatus(accounts);
  const visibleAccounts = accounts.slice(0, 3);
  const overflow = Math.max(0, accounts.length - 3);
  const isDeletingAccount = busy === "disconnect";

  return (
    <div className="mail-settings-card-wrap">
    <Card>
      <div className={isDeletingAccount ? "mail-settings-card-content is-disabled" : "mail-settings-card-content"} aria-busy={isDeletingAccount}>
      <div className="settings-card-head">
        <div className="settings-card-title-row">
          <div className="settings-card-icon">
            <Inbox size={20} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
              Mail 連携
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              会社メールアドレスの受信・返信・AI 補助
            </div>
          </div>
        </div>
        <IntegrationStatusBadge status={status} />
      </div>

      {(loadError || testError) && (
        <div
          style={{
            padding: "10px 12px",
            background: "#fef2f2",
            color: "#b91c1c",
            borderRadius: 8,
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          {loadError || testError}
        </div>
      )}

      {/* 通常表示:アカウント一覧(最大 3 行) */}
      {view.mode === "idle" && (
        <>
          <MailAccountList
            accounts={visibleAccounts}
            onEdit={(id) => setView({ mode: "editing", accountId: id })}
            onDisconnect={handleDisconnect}
            busy={busy === "load" || busy === "disconnect" ? busy : null}
            overflow={overflow}
          />
          <hr className="divider" />
          <div className="row gap-8">
            <Button
              iconLeft={<Plus size={13} />}
              onClick={() => {
                setTestError(null);
                setView({ mode: "creating" });
              }}
              disabled={busy !== null}
            >
              メールアカウントを追加
            </Button>
            <Button
              variant="ghost"
              iconLeft={<RefreshCw size={13} />}
              onClick={load}
              disabled={busy !== null}
            >
              再読み込み
            </Button>
            <Button
              variant="ghost"
              iconLeft={<RefreshCw size={13} />}
              onClick={handleRepair}
              disabled={busy !== null || accounts.length === 0}
            >
              修復
            </Button>
          </div>
        </>
      )}

      <MailAccountFormModal
        open={view.mode === "creating" || view.mode === "editing"}
        title={view.mode === "editing" ? "メールアカウントを編集" : "メールアカウントを追加"}
        account={editingAccount}
        onSave={handleSave}
        onCancel={() => setView({ mode: "idle" })}
        onTestImap={(b) => runTest("imap", b)}
        onTestSmtp={(b) => runTest("smtp", b)}
        allowedAccountTypes={allowedAccountTypes}
        busy={
          busy === "save"
            ? "save"
            : busy === "test-imap"
              ? "test-imap"
              : busy === "test-smtp"
                ? "test-smtp"
                : null
        }
      />
      </div>
    </Card>
      {isDeletingAccount && (
        <div className="mail-settings-card-blocker" role="status" aria-live="polite">
          <RefreshCw size={16} className="mail-settings-card-blocker__icon" />
          <span>削除中...</span>
        </div>
      )}
    </div>
  );
};
